/**
 * RoadmapFilterBar — the search box and the status / priority / tag / owner
 * filter chips.
 *
 * Extracted from the grid so the kanban board filters identically: both views
 * render this same component against filter state owned by RoadmapManager.
 * Purely presentational — every change is reported upward, including the raw
 * (undebounced) search text; the debounce lives with the state in the manager.
 */
import {
  OWNER_OPTIONS,
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  OWNER_LABELS,
  STATUS_LABELS,
} from './roadmapMeta';
import type { RoadmapOwner, RoadmapPriority, RoadmapStatus } from '../../types';

interface RoadmapFilterBarProps {
  /** Raw input text, not the debounced query the list actually filters on. */
  search: string;
  onSearchChange: (search: string) => void;
  statusFilter: RoadmapStatus[];
  priorityFilter: RoadmapPriority[];
  tagFilter: string[];
  ownerFilter: RoadmapOwner | 'all';
  /** Every tag present in the loaded data. Empty ⇒ the tag row is hidden. */
  allTags: string[];
  onToggleStatus: (status: RoadmapStatus) => void;
  onTogglePriority: (priority: RoadmapPriority) => void;
  onToggleTag: (tag: string) => void;
  onOwnerChange: (owner: RoadmapOwner | 'all') => void;
  onClear: () => void;
}

export function RoadmapFilterBar({
  search,
  onSearchChange,
  statusFilter,
  priorityFilter,
  tagFilter,
  ownerFilter,
  allTags,
  onToggleStatus,
  onTogglePriority,
  onToggleTag,
  onOwnerChange,
  onClear,
}: RoadmapFilterBarProps) {
  const filtersActive =
    search.trim().length > 0 ||
    statusFilter.length > 0 ||
    priorityFilter.length > 0 ||
    tagFilter.length > 0 ||
    ownerFilter !== 'all';

  const chipClass = (active: boolean) =>
    `px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
      active
        ? 'bg-brand-600 text-white'
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`;

  return (
    <div className="space-y-2 border border-gray-200 rounded-lg bg-white p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-500 w-16">Search</span>
        <div className="relative flex-1 min-w-[16rem]">
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search title, description, tags, content…"
            className="w-full border border-gray-300 rounded-lg pl-3 pr-8 py-1 text-xs focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          {search.length > 0 && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 leading-none"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-500 w-16">Status</span>
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onToggleStatus(s)}
            className={chipClass(statusFilter.includes(s))}
          >
            {STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-500 w-16">Priority</span>
        {PRIORITY_OPTIONS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onTogglePriority(p)}
            className={chipClass(priorityFilter.includes(p))}
          >
            {p}
          </button>
        ))}
      </div>

      {allTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 w-16">Tags</span>
          {allTags.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => onToggleTag(tag)}
              className={chipClass(tagFilter.includes(tag))}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-500 w-16">Owner</span>
        <select
          value={ownerFilter}
          onChange={(e) => onOwnerChange(e.target.value as RoadmapOwner | 'all')}
          className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white"
        >
          <option value="all">Anyone</option>
          {OWNER_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {OWNER_LABELS[o]}
            </option>
          ))}
        </select>
        {filtersActive && (
          <button
            type="button"
            onClick={onClear}
            className="ml-auto text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}
